import { useState } from 'react'
import { ProviderMaintenanceItem } from '@/modules/bank/type'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import useMutationRejectMaintenance from '@/modules/bank/api/hooks/useMutationRejectMaintenance'
import { Loader2 } from 'lucide-react'
import MaintenanceKeyConfirmDialog from '../MaintenanceKeyConfirmDialog'
import showErrorToast from '@/common/utils/showErrorToast'

interface MaintenanceTableProps {
	data: ProviderMaintenanceItem[]
	isLoading?: boolean
}

const typeLabel: Record<string, string> = {
	DEPOSIT: 'ฝากเงิน',
	WITHDRAWAL: 'ถอนเงิน',
}

export default function MaintenanceTable({
	data,
	isLoading,
}: MaintenanceTableProps) {
	const { mutateAsync: rejectMaintenance, isPending: isRejecting } =
		useMutationRejectMaintenance()
	const [confirmOpen, setConfirmOpen] = useState(false)
	const [rejectId, setRejectId] = useState<string | null>(null)

	const handleRejectClick = (id: string) => {
		setRejectId(id)
		setConfirmOpen(true)
	}

	const handleConfirmReject = async (maintenanceKey: string) => {
		if (!rejectId) return
		try {
			await rejectMaintenance({ id: rejectId, maintenanceKey })
			setRejectId(null)
			setConfirmOpen(false)
		} catch (err) {
			showErrorToast(err as Error)
		}
	}

	return (
		<>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>ผู้ให้บริการ</TableHead>
						<TableHead>ประเภท</TableHead>
						<TableHead>เริ่มต้น</TableHead>
						<TableHead>สิ้นสุด</TableHead>
						<TableHead className="w-[100px]">ดำเนินการ</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell colSpan={5} className="text-center">
								<Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
							</TableCell>
						</TableRow>
					) : data.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={5}
								className="text-center text-muted-foreground"
							>
								ไม่มีรายการซ่อมบำรุง
							</TableCell>
						</TableRow>
					) : (
						data.map((item) => (
							<TableRow key={item.id}>
								<TableCell>
									<Badge variant="outline">{item.bankProviderName}</Badge>
								</TableCell>
								<TableCell>{typeLabel[item.type] ?? item.type}</TableCell>
								<TableCell>
									{format(new Date(item.startAt), 'dd/MM/yyyy HH:mm', {
										locale: th,
									})}
								</TableCell>
								<TableCell>
									{item.endAt
										? format(new Date(item.endAt), 'dd/MM/yyyy HH:mm', {
												locale: th,
											})
										: 'จนกว่าจะยกเลิก'}
								</TableCell>
								<TableCell>
									<Button
										variant="outline"
										size="sm"
										onClick={() => handleRejectClick(item.id)}
										disabled={isRejecting}
									>
										{isRejecting ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											'ยกเลิกการซ่อมบำรุง'
										)}
									</Button>
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
			<MaintenanceKeyConfirmDialog
				open={confirmOpen}
				onOpenChange={(open) => {
					setConfirmOpen(open)
					if (!open) setRejectId(null)
				}}
				title="ยืนยันการยกเลิกการซ่อมบำรุง"
				description="กรอกรหัสยืนยัน (BANK_MAINTENANCE_KEY) เพื่อดำเนินการ"
				confirmLabel="ยืนยันยกเลิก"
				onConfirm={handleConfirmReject}
				isPending={isRejecting}
			/>
		</>
	)
}
